/**
 * Paylasimli bicim/sayisal yardimcilar — eski `frontend/public/js/utils.js`
 * (window.vddUtils) ile ayni adlandirma ve TR yerel ayari korunur.
 */

export function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  if (min > max) return min;
  return Math.min(max, Math.max(min, value));
}

/** 0-100 araligina kirpip yuzde olarak dondurur (ör. saglik halkasi, ilerleme cubugu). */
export function clampPercent(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value)) return 0;
  return clamp(value, 0, 100);
}

export function fmtInt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("tr-TR");
}

export function fmtMoney(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return "₺" + Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 0 });
}

export function fmtPct(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return (Number(n) * 100).toFixed(1) + "%";
}

export function fmtFloat(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 1 });
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * "gg.aa" kisa tarih bicimi (yilsiz) — grafik eksen etiketleri icin
 * (CiroTrendi/TeslimatChart). `YYYY-MM-DD` girdisi DOGRUDAN string'ten
 * kesilir — `new Date("YYYY-MM-DD")` UTC gece yarisi olarak ayristigindan
 * Turkiye (UTC+3) gibi doguya kayan saat dilimlerinde `toLocaleDateString`
 * bir onceki gunu gosterebilir; tarih-saatli ISO ve diger bicimler icin
 * `Date` ile ayristirilir.
 */
export function fmtDateShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const saltMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (saltMatch) return `${saltMatch[3]}.${saltMatch[2]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit" });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** "x dk once / x sa once / şimdi" — Freshness bileseni icin goreli zaman. */
export function fmtRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "—";
  const diffMs = now.getTime() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "şimdi";
  if (diffMin < 60) return `${diffMin} dk önce`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} sa önce`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD} gün önce`;
}

export function escapeHtml(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      case "'":
        return "&#39;";
      default:
        return c;
    }
  });
}
